/*
 * Copyright 2025 SJTU-Geek
 * Copyright 2020-2025 SJTU-Plus
 *
 * ----------------------------------------------------------------
 * Modified by Niazye from ECNU-minus on 2025-12-12
 * Changes:
 * - fix: hide unusable filters
 *
 *
 * Modified by 霧雨バニラ from BetterECNU on 2026-05-23
 * Changes:
 * - fix: 异步定时器安全优化
 *
 * Copyright 2025 ECNU-minus
 * ----------------------------------------------------------------
 */

import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import React, { useEffect, useRef, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { useParams } from 'react-router-dom'

import LessonRow from './LessonRow'
import { filterDataForm, useLessonConversion, useLessonData } from './Utils'

const tableHeader = [
  '课号',
  '开课院系',
  '教师姓名',
  '课程',
  '学时/学分',
  '上课时间地点',
  '备注',
  // '年级',
  '教学组成',
]

function DataLoaded({ loaded, size }) {
  if (!loaded) {
    return (
      <div>
        <div className='spinner-border spinner-border-sm mr-3 text-muted'></div>
        <span className='text-muted'>正在加载数据……</span>
      </div>
    )
  } else {
    return (
      <div>
        <div className='d-flex align-items-center justify-content-center mb-3'>
          <span className='text-muted'>以上为全部 {size} 条记录</span>
        </div>
      </div>
    )
  }
}

export default ({ filterData, state, setState }) => {
  const { semester } = useParams()
  const { data: lessonsRaw } = useLessonData(semester)
  const [entries, setEntries] = useState(20)
  const timerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  const handleStateChange = ({ name, value }) => {
    const set = new Set(state)
    if (value) {
      set.add(name)
    } else {
      set.delete(name)
    }
    setState(set)
  }

  const { data: lessonConversion } = useLessonConversion()

  if (lessonsRaw) {
    const lessons = filterDataForm(
      uniqBy(lessonsRaw, 'jxbmc'),
      filterData,
      lessonConversion
    )
    const truncatedLessons = sortBy(lessons, 'kch').slice(0, entries)

    const moreEntries = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(
        () =>
          setEntries((prev) =>
            Math.max(Math.min(prev + 100, lessons.length), 100)
          ),
        300
      )
    }

    return (
      <>
        <p className='text-center mt-3'>共 {lessons.length} 条记录</p>
        <div className='table-responsive-md'>
          <table className='table table-sm small sjtu-table'>
            <thead>
              <tr>
                {tableHeader.map((header) => (
                  <th scope='col' className='table-header' key={header}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {truncatedLessons.map((lesson) => (
                <LessonRow
                  lesson={lesson}
                  key={lesson.jxbmc}
                  name={lesson.jxbmc}
                  state={state.has(lesson.jxbmc)}
                  setState={handleStateChange}
                  lessonConversion={lessonConversion}
                ></LessonRow>
              ))}
            </tbody>
          </table>
          <InView
            as='div'
            onChange={(inView) => {
              if (inView) moreEntries()
            }}
            className='d-flex align-items-center justify-content-center mb-3'
          >
            <DataLoaded
              loaded={entries >= lessons.length}
              size={lessons.length}
            ></DataLoaded>
          </InView>
        </div>
      </>
    )
  } else {
    return <div></div>
  }
}
